import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Search, X, Building2, TrendingUp, User, FileText, BriefcaseBusiness, ReceiptText, Workflow, Navigation, History, UserPlus } from 'lucide-react'
import FocusTrap from 'focus-trap-react'
import { apiClient } from '@/lib/api'
import { Avatar } from '@/components/ui/Avatar'
import { Badge, type BadgeColor } from '@/components/ui/Badge'
import type { ApiContact, ApiCompany, ApiDeal, ApiQuote, ApiJob, ApiInvoice, ApiWorkflow, PaginatedApiResponse } from '@/lib/apiTypes'
import { logger } from '../../lib/logger'
import { SafeAreaTop } from '@/components/ui/SafeAreaTop'
import { statusLabel } from '@/lib/labels'
import { formatMoney } from '@/lib/format'
import { buildOpportunities, opportunityStatusLabel, OPPORTUNITY_STAGE_TONE } from '@/lib/opportunity'
import { readRecents, rememberRecent, type RecentRecord } from '@/lib/recentRecords'
import { usePermissions } from '@/hooks/usePermission'
import { allowedCreateActions } from '@/components/Navigation/CreateMenu'
import { navItems, visibleNavItems } from '@/components/Navigation/navItems'
import { usePresence } from '@/hooks/usePresence'
import { cn } from '@/lib/utils'

/** Close: a short fade, not a one-frame cut (UX-117). */
const SEARCH_EXIT_MS = 160

// ─────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean
  onClose: () => void
}

type ResultSection = 'contact' | 'company' | 'deal' | 'quote' | 'job' | 'invoice' | 'workflow' | 'destination' | 'recent' | 'action'

/** Sections that are records worth listing under "Recent". */
const RECORD_SECTIONS = new Set<ResultSection>(['contact', 'company', 'deal', 'quote', 'job', 'invoice'])

const RECENT_ICON: Record<string, ReactNode> = {
  contact: <User className="w-4 h-4" />,
  company: <Building2 className="w-4 h-4" />,
  deal: <TrendingUp className="w-4 h-4" />,
  quote: <FileText className="w-4 h-4" />,
  job: <BriefcaseBusiness className="w-4 h-4" />,
  invoice: <ReceiptText className="w-4 h-4" />,
}

/**
 * "No results" → offer to add the contact. A phone number or email carries
 * over into the new-contact form; a name can't be prefilled there yet, so
 * the row just opens the form.
 */
export function createContactPath(query: string): string {
  const q = query.trim()
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(q)) return `/customers/people?prefill_email=${encodeURIComponent(q)}`
  if (q.replace(/\D/g, '').length >= 7 && /^[\d\s()+.-]+$/.test(q)) return `/customers/people?prefill_phone=${encodeURIComponent(q)}`
  return '/customers/people?new=1'
}

interface FlatResult {
  id: string
  section: ResultSection
  label: string
  sub?: string
  path?: string
}

interface DestinationResult {
  id: string
  label: string
  sub: string
  path: string
  keywords: string
}

// ─────────────────────────────────────────────────────────
// Opportunity stage + amount (same classification as everywhere else)
// ─────────────────────────────────────────────────────────

export interface SearchOpportunityView {
  /** "Invoiced", "Quoted · Not sent yet", "Completed": the contact page's words. */
  label: string
  tone: BadgeColor
  /** Invoice total, else quote total, else the estimate (DECISIONS §8). 0 = show none. */
  value: number
}

/**
 * A deal row in search reads its stage and amount from its documents via
 * lib/opportunity, exactly like the contact page and the Work board. Reading
 * the raw `deal.stage` / `deal.value` showed "Quoted $48K" for a deal the
 * contact page called "Invoiced · $12,840" (rc3 M2).
 */
export function searchOpportunityView(
  deal: ApiDeal,
  docs: { quotes: ApiQuote[]; jobs: ApiJob[]; invoices: ApiInvoice[]; docsHidden?: boolean },
): SearchOpportunityView {
  // The docs are the whole workspace's: buildOpportunities also returns a
  // chain for every document no deal claims, so pick this deal's own.
  const o = buildOpportunities({ deals: [deal], ...docs }).find((x) => x.id === `deal:${deal.id}`)!
  return { label: opportunityStatusLabel(o), tone: OPPORTUNITY_STAGE_TONE[o.stage], value: o.value }
}


export function getGlobalSearchPath(result: Pick<FlatResult, 'id' | 'section' | 'path'>): string {
  if (result.path) return result.path
  if (result.section === 'contact') return `/customers/people/${result.id}`
  if (result.section === 'company') return `/customers/companies/${result.id}`
  if (result.section === 'deal') return `/work/${encodeURIComponent(`deal:${result.id}`)}`
  if (result.section === 'quote') return `/work/${encodeURIComponent(`q:${result.id}`)}`
  if (result.section === 'job') return `/work/${encodeURIComponent(`job:${result.id}`)}`
  if (result.section === 'invoice') return `/work/${encodeURIComponent(`inv:${result.id}`)}`
  if (result.section === 'workflow') return '/workflows'
  return '/'
}

// ─────────────────────────────────────────────────────────
// Cached data store (module-level so it persists across opens)
// ─────────────────────────────────────────────────────────

let cachedContacts: ApiContact[] | null = null
let cachedCompanies: ApiCompany[] | null = null
let cachedDeals: ApiDeal[] | null = null
let cachedQuotes: ApiQuote[] | null = null
let cachedJobs: ApiJob[] | null = null
let cachedInvoices: ApiInvoice[] | null = null
let cachedWorkflows: ApiWorkflow[] | null = null
let fetchPromise: Promise<void> | null = null

const destinations: DestinationResult[] = [
  { id: 'dashboard', label: 'Home', sub: 'Today, what needs attention, money owed', path: '/dashboard', keywords: 'home dashboard overview today schedule money owed receivables' },
  { id: 'work', label: 'Work', sub: 'Leads, quotes, jobs, invoices', path: '/work', keywords: 'work leads deals quotes jobs invoices funnel pipeline' },
  { id: 'customers', label: 'Contacts', sub: 'Customers, contractors and company contacts', path: '/customers', keywords: 'customers people contacts companies clients' },
  { id: 'calendar', label: 'Calendar', sub: 'Schedule jobs', path: '/calendar', keywords: 'calendar schedule jobs appointments agenda' },
  { id: 'inbox', label: 'Inbox', sub: 'Texts, email, campaigns, forms', path: '/inbox', keywords: 'inbox messages texts email campaigns forms' },
  { id: 'workflows', label: 'Workflows', sub: 'Automations and history', path: '/workflows', keywords: 'workflows automations automation triggers actions history' },
  { id: 'marketing', label: 'Marketing', sub: 'Reviews, social posts, and the growth calendar', path: '/marketing', keywords: 'marketing reviews social posts campaigns growth calendar digest' },
  { id: 'reports', label: 'Reports', sub: 'Performance and finance', path: '/reports', keywords: 'reports analytics charts revenue invoices jobs' },
  { id: 'settings', label: 'Settings', sub: 'Account and business setup', path: '/settings', keywords: 'settings account setup profile team billing phone twilio a2p brand security notifications' },
  { id: 'phone-settings', label: 'Phone settings', sub: 'Numbers, routing, AI assistant', path: '/settings/phone', keywords: 'phone twilio numbers routing assistant calls sms' },
  { id: 'brand-settings', label: 'Brand kit', sub: 'Logo, colors, quote PDF identity', path: '/settings/brand', keywords: 'brand kit logo colors pdf quote invoice' },
  { id: 'business-settings', label: 'Business profile', sub: 'Name, phone, address, license', path: '/settings/business', keywords: 'business profile company legal name license address phone email website time zone' },
  { id: 'money-settings', label: 'Tax & payments', sub: 'Sales tax, card fee, Stripe, QuickBooks', path: '/settings/money', keywords: 'sales tax rate card fee stripe quickbooks how to pay payments' },
  { id: 'pricing-settings', label: 'Services & pricing', sub: 'Service types and price book', path: '/settings/pricing', keywords: 'price book catalog services pricing taxable' },
  { id: 'booking-settings', label: 'Online booking', sub: 'Booking link, appointments, reminders', path: '/settings/booking', keywords: 'booking page link appointments reminders qr' },
  { id: 'team-settings', label: 'Team', sub: 'People and roles', path: '/settings/team', keywords: 'team users invite roles permissions' },
]

function readItems<T>(
  result: PromiseSettledResult<{ data: { data: PaginatedApiResponse<T> } }>,
): T[] {
  if (result.status !== 'fulfilled') return []
  return result.value.data.data.items ?? []
}

async function fetchAllData(): Promise<void> {
  if (fetchPromise) return fetchPromise
  fetchPromise = (async () => {
    try {
      const [contactsRes, companiesRes, dealsRes, quotesRes, jobsRes, invoicesRes, workflowsRes] = await Promise.allSettled([
        apiClient.get('/api/contacts', { params: { take: 100 } }),
        apiClient.get('/api/companies', { params: { take: 100 } }),
        apiClient.get('/api/deals', { params: { take: 100 } }),
        apiClient.get('/api/quotes', { params: { take: 100 } }),
        apiClient.get('/api/jobs', { params: { take: 100 } }),
        apiClient.get('/api/invoices', { params: { take: 100 } }),
        apiClient.get('/api/workflows', { params: { take: 100 } }),
      ])
      cachedContacts = readItems<ApiContact>(contactsRes)
      cachedCompanies = readItems<ApiCompany>(companiesRes)
      cachedDeals = readItems<ApiDeal>(dealsRes)
      cachedQuotes = readItems<ApiQuote>(quotesRes)
      cachedJobs = readItems<ApiJob>(jobsRes)
      cachedInvoices = readItems<ApiInvoice>(invoicesRes)
      cachedWorkflows = readItems<ApiWorkflow>(workflowsRes)
    } catch {
      // Fail silently — search just returns empty
      cachedContacts = cachedContacts ?? []
      cachedCompanies = cachedCompanies ?? []
      cachedDeals = cachedDeals ?? []
      cachedQuotes = cachedQuotes ?? []
      cachedJobs = cachedJobs ?? []
      cachedInvoices = cachedInvoices ?? []
      cachedWorkflows = cachedWorkflows ?? []
    }
  })()
  return fetchPromise
}

// ─────────────────────────────────────────────────────────
// Search filter helpers
// ─────────────────────────────────────────────────────────

function matchesQuery(haystack: string, query: string): boolean {
  return haystack.toLowerCase().includes(query.toLowerCase())
}

function contactName(contact?: { firstName: string; lastName: string } | null): string {
  return contact ? `${contact.firstName} ${contact.lastName}`.trim() : ''
}

function filterContacts(contacts: ApiContact[], query: string): ApiContact[] {
  if (!query) return []
  return contacts
    .filter(c =>
      matchesQuery(`${c.firstName} ${c.lastName} ${c.email ?? ''} ${c.phone ?? ''} ${c.company?.name ?? ''}`, query)
    )
    .slice(0, 5)
}

function filterCompanies(companies: ApiCompany[], query: string): ApiCompany[] {
  if (!query) return []
  return companies
    .filter(c => matchesQuery(`${c.name} ${c.industry ?? ''}`, query))
    .slice(0, 5)
}

function filterDeals(deals: ApiDeal[], query: string): ApiDeal[] {
  if (!query) return []
  return deals
    .filter(d => matchesQuery(`${d.name} ${d.company?.name ?? ''} ${contactName(d.contact)} ${d.stage}`, query))
    .slice(0, 5)
}

function filterQuotes(quotes: ApiQuote[], query: string): ApiQuote[] {
  if (!query) return []
  return quotes
    .filter(q => matchesQuery(`${q.quoteNumber ?? ''} ${contactName(q.contact)} ${q.company?.name ?? ''} ${q.serviceAddress ?? ''} ${q.status}`, query))
    .slice(0, 5)
}

function filterJobs(jobs: ApiJob[], query: string): ApiJob[] {
  if (!query) return []
  return jobs
    .filter(j => matchesQuery(`${j.address} ${contactName(j.contact)} ${j.company?.name ?? ''} ${j.status}`, query))
    .slice(0, 5)
}

function filterInvoices(invoices: ApiInvoice[], query: string): ApiInvoice[] {
  if (!query) return []
  return invoices
    .filter(i => matchesQuery(`${i.invoiceNumber} ${contactName(i.contact)} ${i.company?.name ?? ''} ${i.status} ${i.notes ?? ''}`, query))
    .slice(0, 5)
}

function filterWorkflows(workflows: ApiWorkflow[], query: string): ApiWorkflow[] {
  if (!query) return []
  return workflows
    .filter(w => matchesQuery(`${w.name} ${w.description ?? ''} ${w.isActive ? 'active' : 'inactive'}`, query))
    .slice(0, 5)
}

function filterDestinations(query: string): DestinationResult[] {
  if (!query) return []
  return destinations
    .filter(d => matchesQuery(`${d.label} ${d.sub} ${d.keywords}`, query))
    .slice(0, 5)
}

// ─────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────

// CRM resource roots whose invalidation should refresh the global-search cache (FND-027).
const SEARCH_RESOURCE_ROOTS = ['contacts', 'companies', 'deals', 'quotes', 'jobs', 'invoices', 'workflows'] as const

export default function GlobalSearchModal({ isOpen, onClose }: Props) {
  const { mounted, leaving } = usePresence(isOpen, SEARCH_EXIT_MS)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [dataReady, setDataReady] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [recents, setRecents] = useState<RecentRecord[]>([])
  const { can } = usePermissions()

  // Filtered results
  const contacts = dataReady ? filterContacts(cachedContacts ?? [], debouncedQuery) : []
  const companies = dataReady ? filterCompanies(cachedCompanies ?? [], debouncedQuery) : []
  const deals = dataReady ? filterDeals(cachedDeals ?? [], debouncedQuery) : []
  const quotes = dataReady ? filterQuotes(cachedQuotes ?? [], debouncedQuery) : []
  const jobs = dataReady ? filterJobs(cachedJobs ?? [], debouncedQuery) : []
  const invoices = dataReady ? filterInvoices(cachedInvoices ?? [], debouncedQuery) : []
  const workflows = dataReady ? filterWorkflows(cachedWorkflows ?? [], debouncedQuery) : []
  const destinationResults = filterDestinations(debouncedQuery)
  const totalResults =
    contacts.length +
    companies.length +
    deals.length +
    quotes.length +
    jobs.length +
    invoices.length +
    workflows.length +
    destinationResults.length

  const contactResults: FlatResult[] = contacts.map(c => ({
      id: c.id,
      section: 'contact' as ResultSection,
      label: `${c.firstName} ${c.lastName}`,
      sub: c.email,
    }))
  const companyResults: FlatResult[] = companies.map(c => ({
      id: c.id,
      section: 'company' as ResultSection,
      label: c.name,
      sub: c.industry,
    }))
  const dealDocs = {
    quotes: cachedQuotes ?? [],
    jobs: cachedJobs ?? [],
    invoices: cachedInvoices ?? [],
    docsHidden: !can('viewMoney'),
  }
  const dealViews = new Map(deals.map(d => [d.id, searchOpportunityView(d, dealDocs)]))
  const dealResults: FlatResult[] = deals.map(d => ({
      id: d.id,
      section: 'deal' as ResultSection,
      label: d.name,
      sub: dealViews.get(d.id)?.label,
    }))
  const quoteResults: FlatResult[] = quotes.map(q => ({
      id: q.id,
      section: 'quote' as ResultSection,
      label: q.quoteNumber || 'Quote',
      sub: `${contactName(q.contact) || q.company?.name || 'Quote'} · ${statusLabel('quote', q.status)}`,
    }))
  const jobResults: FlatResult[] = jobs.map(j => ({
      id: j.id,
      section: 'job' as ResultSection,
      label: j.address || contactName(j.contact) || 'Job',
      sub: statusLabel('job', j.status),
    }))
  const invoiceResults: FlatResult[] = invoices.map(i => ({
      id: i.id,
      section: 'invoice' as ResultSection,
      label: i.invoiceNumber,
      sub: `${contactName(i.contact) || i.company?.name || 'Invoice'} · ${statusLabel('invoice', i.status)}`,
    }))
  const workflowResults: FlatResult[] = workflows.map(w => ({
      id: w.id,
      section: 'workflow' as ResultSection,
      label: w.name,
      sub: w.isActive ? 'Active workflow' : 'Inactive workflow',
    }))
  const navResults: FlatResult[] = destinationResults.map(d => ({
      id: d.id,
      section: 'destination' as ResultSection,
      label: d.label,
      sub: d.sub,
      path: d.path,
    }))

  // Before typing (UX-115): recent records, create actions and pages — the
  // "jump to" the sidebar's "Search or jump to" promises.
  const zeroState = !query.trim() && !debouncedQuery
  const recentResults: FlatResult[] = zeroState
    ? recents.map((r) => ({ id: r.key, section: 'recent' as ResultSection, label: r.label, sub: r.sub, path: r.path }))
    : []
  const actionResults: FlatResult[] = zeroState
    ? allowedCreateActions(can).map((a) => ({ id: a.kind, section: 'action' as ResultSection, label: a.label, path: a.to }))
    : []
  const gotoResults: FlatResult[] = zeroState
    ? visibleNavItems(navItems, can).map((n) => ({ id: n.path, section: 'destination' as ResultSection, label: n.label, path: n.path }))
    : []
  const noResults = !loading && !!debouncedQuery && totalResults === 0
  const noResultActions: FlatResult[] = noResults && can('createContact')
    ? [{ id: 'new-contact', section: 'action', label: `Add “${debouncedQuery.trim()}” as a new contact`, path: createContactPath(debouncedQuery) }]
    : []

  // Flat list for keyboard navigation
  const flatResults: FlatResult[] = zeroState ? [...recentResults, ...actionResults, ...gotoResults] : noResults ? noResultActions : [
    ...contactResults,
    ...companyResults,
    ...dealResults,
    ...quoteResults,
    ...jobResults,
    ...invoiceResults,
    ...workflowResults,
    ...navResults,
  ]

  // Debounce query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(timer)
  }, [query])

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(-1)
  }, [debouncedQuery])

  // Reset + refetch helper — clears module-level cache then re-runs fetchAllData.
  const resetAndFetch = useCallback(() => {
    cachedContacts = null
    cachedCompanies = null
    cachedDeals = null
    cachedQuotes = null
    cachedJobs = null
    cachedInvoices = null
    cachedWorkflows = null
    fetchPromise = null
    setDataReady(false)
    setLoading(true)
    return fetchAllData()
      .then(() => {
        setDataReady(true)
        setLoading(false)
      })
      .catch((err) => {
        logger.error('[GlobalSearchModal] fetchAllData failed:', err)
        setDataReady(true)   // show empty results rather than infinite spinner
        setLoading(false)
      })
  }, [])

  // Fetch on every open
  useEffect(() => {
    if (!isOpen) return
    setRecents(readRecents())
    void resetAndFetch()
  }, [isOpen, resetAndFetch])

  // FND-027: keep the module-level cache in sync with CRM mutations elsewhere
  // in the app. Subscribe to the TanStack QueryCache and refetch when any
  // resource root the modal depends on is invalidated.
  useEffect(() => {
    if (!isOpen) return
    const cache = queryClient.getQueryCache()
    const unsub = cache.subscribe((event) => {
      if (event.type !== 'updated') return
      // Only react to invalidations of the relevant resource roots.
      const action = event.action as { type?: string } | undefined
      if (action?.type !== 'invalidate') return
      const key = event.query.queryKey
      if (!Array.isArray(key) || typeof key[0] !== 'string') return
      if (!SEARCH_RESOURCE_ROOTS.includes(key[0] as typeof SEARCH_RESOURCE_ROOTS[number])) return
      void resetAndFetch()
    })
    return unsub
  }, [isOpen, queryClient, resetAndFetch])

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 10)
      setQuery('')
      setDebouncedQuery('')
      setActiveIndex(-1)
    }
  }, [isOpen])

  // Body scroll lock while open
  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [isOpen])

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex(i => Math.min(i + 1, flatResults.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex(i => Math.max(i - 1, -1))
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault()
        const result = flatResults[activeIndex]
        if (result) navigateToResult(result)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flatResults, activeIndex]
  )

  function navigateToResult(result: FlatResult) {
    const path = getGlobalSearchPath(result)
    if (RECORD_SECTIONS.has(result.section) && result.label) {
      rememberRecent({ key: `${result.section}:${result.id}`, label: result.label, sub: result.sub, path, section: result.section })
    }
    onClose()
    navigate(path)
  }

  function navigateTo(section: ResultSection, id: string, label: string, sub?: string) {
    navigateToResult({ id, section, label, sub })
  }

  // Compute global index offset per section for keyboard active state
  const contactOffset = 0
  const companyOffset = contacts.length
  const dealOffset = contacts.length + companies.length
  const quoteOffset = dealOffset + deals.length
  const jobOffset = quoteOffset + quotes.length
  const invoiceOffset = jobOffset + jobs.length
  const workflowOffset = invoiceOffset + invoices.length
  const destinationOffset = workflowOffset + workflows.length

  const TITLE_ID = 'global-search-modal-title'

  const renderResultSection = (
    title: string,
    results: FlatResult[],
    offset: number,
    icon: ReactNode,
    iconFor?: (r: FlatResult) => ReactNode | undefined,
  ) => {
    if (results.length === 0) return null
    if (loading && !results.some((r) => r.section === 'recent' || r.section === 'action' || (r.section === 'destination' && zeroState))) return null
    const hasPrevious = offset > 0
    return (
      <section className="py-2">
        {hasPrevious && <div className="hairline-t mx-4 mb-2" />}
        {title && <p className="eyebrow px-5 py-2">{title}</p>}
        {results.map((result, idx) => {
          const globalIdx = offset + idx
          const isActive = globalIdx === activeIndex
          return (
            <button
              key={`${result.section}:${result.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 cursor-pointer rounded-lg transition-colors text-left mx-1 ${
                isActive ? 'bg-white/[0.07]' : 'hover:bg-white/[0.04]'
              }`}
              onClick={() => navigateToResult(result)}
              onMouseEnter={() => setActiveIndex(globalIdx)}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgb(var(--fg-rgb) / 0.05)', border: '0.5px solid rgb(var(--fg-rgb) / 0.10)', color: 'rgb(var(--fg-rgb) / 0.70)' }}>
                {iconFor?.(result) ?? icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink-100 truncate">{result.label}</p>
                {result.sub && <p className="text-xs text-white/[0.62] truncate">{result.sub}</p>}
              </div>
            </button>
          )
        })}
      </section>
    )
  }

  if (!mounted) return null

  return (
    <FocusTrap active={isOpen} focusTrapOptions={{ returnFocusOnDeactivate: true, allowOutsideClick: true }}>
      {/* Overlay — also serves as the dialog root. It fades out on close
          instead of vanishing in one frame (UX-117). */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        aria-hidden={leaving || undefined}
        className={cn(
          'fixed inset-0 bg-midnight-bg/60 backdrop-blur-sm z-[80] flex flex-col md:flex-row md:items-start md:justify-center md:pt-24',
          leaving ? 'animate-fade-out pointer-events-none' : 'animate-fade-in',
        )}
        onClick={onClose}
      >
        {/* Panel — stop click propagation so clicking inside doesn't close.
            SafeAreaTop owns env(safe-area-inset-top). */}
        <SafeAreaTop
          className={cn(
            'glass-popup w-full overflow-hidden flex flex-col h-full max-md:bg-black md:h-auto md:max-w-2xl md:mx-4 md:rounded-2xl rounded-none',
            leaving ? 'md:animate-overlay-out' : 'md:animate-popover-in',
          )}
          onClick={e => e.stopPropagation()}
        >
          {/* Visually hidden title for aria-labelledby */}
          <span id={TITLE_ID} className="sr-only">Global search</span>

          {/* Search input row */}
          <div className="flex items-center gap-3 px-5 py-4">
            <Search className="w-5 h-5 text-ink-400 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search customers, jobs, invoices…"
              aria-label="Search"
              className="flex-1 text-xl bg-transparent text-ink-50 outline-none placeholder:text-white/40"
              autoComplete="off"
              spellCheck={false}
            />
            <div className="flex items-center gap-2 shrink-0">
              {/* X always visible: clears query if non-empty, closes modal if already empty */}
              <button
                onClick={() => {
                  if (query) {
                    setQuery('')
                    inputRef.current?.focus()
                  } else {
                    onClose()
                  }
                }}
                aria-label={query ? 'Clear search' : 'Close search'}
                className="p-1 text-white/[0.62] hover:text-ink-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
              <kbd className="text-xs bg-white/[0.06] px-2 py-0.5 rounded text-white/[0.62] border border-white/[0.08] font-mono hidden sm:inline [@media(pointer:coarse)]:hidden">
                ESC
              </kbd>
            </div>
          </div>

          {/* Divider */}
          <div className="hairline-t" />

          {/* Results */}
          <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto md:flex-none md:max-h-[480px]">
            {/* Loading state */}
            {loading && !zeroState && (
              <div className="flex items-center justify-center py-12">
                <div className="w-5 h-5 rounded-full border-2 border-white/20 border-t-white/60 animate-spin" />
              </div>
            )}

            {/* Before typing: Recent · Create · Go to (UX-115). Shown even while
                the record cache loads — none of it needs the search data. */}
            {zeroState && (
              <>
                {renderResultSection('Recent', recentResults, 0, <History className="w-4 h-4" />, (r) => RECENT_ICON[recents.find((x) => x.key === r.id)?.section ?? ''])}
                {renderResultSection('Create', actionResults, recentResults.length, <UserPlus className="w-4 h-4" />, (r) => {
                  const Icon = allowedCreateActions(can).find((a) => a.kind === r.id)?.icon
                  return Icon ? <Icon className="w-4 h-4" /> : undefined
                })}
                {renderResultSection('Go to', gotoResults, recentResults.length + actionResults.length, <Navigation className="w-4 h-4" />, (r) => {
                  const Icon = navItems.find((n) => n.path === r.id)?.icon
                  return Icon ? <Icon className="w-4 h-4" /> : undefined
                })}
              </>
            )}

            {/* No results */}
            {noResults && (
              <div className="flex flex-col items-center gap-2 pt-10 pb-4 text-center">
                <p className="text-sm text-ink-400">
                  No results for{' '}
                  <span className="text-ink-200 font-medium">"{debouncedQuery}"</span>
                </p>
                <p className="text-xs text-white/[0.62]">Check the spelling, or search by phone number or address.</p>
              </div>
            )}
            {noResults && renderResultSection('', noResultActions, 0, <UserPlus className="w-4 h-4" />)}

            {/* Contacts section */}
            {!loading && contacts.length > 0 && (
              <section className="py-2">
                <p className="eyebrow px-5 py-2">Contacts</p>
                {contacts.map((contact, idx) => {
                  const globalIdx = contactOffset + idx
                  const isActive = globalIdx === activeIndex
                  return (
                    <button
                      key={contact.id}
                      className={`w-full flex items-center gap-3 px-4 py-3 cursor-pointer rounded-lg transition-colors text-left mx-1 ${
                        isActive ? 'bg-white/[0.07]' : 'hover:bg-white/[0.04]'
                      }`}
                      onClick={() => navigateTo('contact', contact.id, `${contact.firstName} ${contact.lastName}`.trim(), contact.email ?? undefined)}
                      onMouseEnter={() => setActiveIndex(globalIdx)}
                    >
                      <Avatar
                        name={`${contact.firstName} ${contact.lastName}`}
                        size="sm"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink-100 truncate">
                          {contact.firstName} {contact.lastName}
                        </p>
                        {contact.email && (
                          <p className="text-xs text-white/[0.62] truncate">{contact.email}</p>
                        )}
                      </div>
                      {contact.company && (
                        <span className="text-xs text-white/[0.62] shrink-0 truncate max-w-[120px]">
                          {contact.company.name}
                        </span>
                      )}
                    </button>
                  )
                })}
              </section>
            )}

            {/* Companies section */}
            {!loading && companies.length > 0 && (
              <section className="py-2">
                {contacts.length > 0 && <div className="hairline-t mx-4 mb-2" />}
                <p className="eyebrow px-5 py-2">Companies</p>
                {companies.map((company, idx) => {
                  const globalIdx = companyOffset + idx
                  const isActive = globalIdx === activeIndex
                  return (
                    <button
                      key={company.id}
                      className={`w-full flex items-center gap-3 px-4 py-3 cursor-pointer rounded-lg transition-colors text-left mx-1 ${
                        isActive ? 'bg-white/[0.07]' : 'hover:bg-white/[0.04]'
                      }`}
                      onClick={() => navigateTo('company', company.id, company.name, company.industry ?? undefined)}
                      onMouseEnter={() => setActiveIndex(globalIdx)}
                    >
                      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgb(var(--fg-rgb) / 0.06)', border: '0.5px solid rgb(var(--fg-rgb) / 0.12)' }}>
                        <Building2 className="w-4 h-4" style={{ color: 'rgb(var(--fg-rgb) / 0.70)' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink-100 truncate">{company.name}</p>
                        {company.industry && (
                          <p className="text-xs text-white/[0.62] truncate">{company.industry}</p>
                        )}
                      </div>
                      {company._count?.contacts !== undefined && (
                        <span className="text-xs text-white/[0.62] shrink-0">
                          {company._count.contacts} contact{company._count.contacts !== 1 ? 's' : ''}
                        </span>
                      )}
                    </button>
                  )
                })}
              </section>
            )}

            {/* Opportunities (deals) section */}
            {!loading && deals.length > 0 && (
              <section className="py-2">
                {(contacts.length > 0 || companies.length > 0) && (
                  <div className="hairline-t mx-4 mb-2" />
                )}
                <p className="eyebrow px-5 py-2">Opportunities</p>
                {deals.map((deal, idx) => {
                  const globalIdx = dealOffset + idx
                  const isActive = globalIdx === activeIndex
                  const view = dealViews.get(deal.id)
                  return (
                    <button
                      key={deal.id}
                      className={`w-full flex items-center gap-3 px-4 py-3 cursor-pointer rounded-lg transition-colors text-left mx-1 ${
                        isActive ? 'bg-white/[0.07]' : 'hover:bg-white/[0.04]'
                      }`}
                      onClick={() => navigateTo('deal', deal.id, deal.name, view?.label)}
                      onMouseEnter={() => setActiveIndex(globalIdx)}
                    >
                      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgb(var(--fg-rgb) / 0.06)', border: '0.5px solid rgb(var(--fg-rgb) / 0.12)' }}>
                        <TrendingUp className="w-4 h-4" style={{ color: 'rgb(var(--fg-rgb) / 0.70)' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink-100 truncate">{deal.name}</p>
                        {deal.company && (
                          <p className="text-xs text-white/[0.62] truncate">{deal.company.name}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {view && <Badge color={view.tone}>{view.label}</Badge>}
                        {view && view.value > 0 && (
                          <span className="text-sm font-medium text-ink-200 tabular-nums">
                            {formatMoney(view.value)}
                          </span>
                        )}
                      </div>
                    </button>
                  )
                })}
              </section>
            )}

            {renderResultSection('Quotes', quoteResults, quoteOffset, <FileText className="w-4 h-4" />)}
            {renderResultSection('Jobs', jobResults, jobOffset, <BriefcaseBusiness className="w-4 h-4" />)}
            {renderResultSection('Invoices', invoiceResults, invoiceOffset, <ReceiptText className="w-4 h-4" />)}
            {renderResultSection('Workflows', workflowResults, workflowOffset, <Workflow className="w-4 h-4" />)}
            {renderResultSection('Go to', navResults, destinationOffset, <Navigation className="w-4 h-4" />)}

            {/* Bottom padding */}
            {!loading && totalResults > 0 && <div className="h-2" />}
          </div>

          {/* Footer hint */}
          {!loading && totalResults > 0 && (
            <>
              <div className="hairline-t" />
              <div className="flex items-center gap-4 px-5 py-2.5">
                <div className="flex items-center gap-1.5">
                  <User className="w-3 h-3 text-white/[0.62]" />
                  <span className="text-xs text-white/[0.62]">{totalResults} result{totalResults !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-1 ml-auto [@media(pointer:coarse)]:hidden">
                  <kbd className="text-xs bg-white/[0.06] px-1.5 py-0.5 rounded text-white/[0.62] border border-white/[0.08] font-mono">↑↓</kbd>
                  <span className="text-xs text-white/[0.62]">navigate</span>
                  <kbd className="text-xs bg-white/[0.06] px-1.5 py-0.5 rounded text-white/[0.62] border border-white/[0.08] font-mono ml-2">↵</kbd>
                  <span className="text-xs text-white/[0.62]">open</span>
                </div>
              </div>
            </>
          )}
        </SafeAreaTop>
      </div>
    </FocusTrap>
  )
}
